import { useEffect, useMemo } from "react";
import {
    keepPreviousData,
    useInfiniteQuery,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
    type UseQueryResult,
} from "@tanstack/react-query";
import {
    getCatalogueStatus,
    getHere,
    getPosting,
    getPostingEvents,
    getPlaces,
    getTitles,
    searchCounts,
    searchPostings,
    type PlaceLevel,
    type SearchPosting,
    type SearchPostingsQuery,
} from "@/lib/api/catalogue";
import { keys } from "./keys";
import { SEARCH_FRESH_MS } from "@/lib/catalogueCache";

export { SEARCH_FRESH_MS } from "@/lib/catalogueCache";

/**
 * Each item once by its key, the first kept, in order. A list can name
 * one twice (a page that shifted under its cursor, a row sent twice); it
 * is one card, and one React key.
 */
export function onceEach<Item>(items: readonly Item[], keyOf: (item: Item) => string): Item[] {
    const seen = new Set<string>();
    return items.filter((item) => {
        const key = keyOf(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** Each posting once, the first kept. */
export function oncePerPosting<Item extends { posting_id: string }>(
    items: readonly Item[],
): Item[] {
    return onceEach(items, (item) => item.posting_id);
}

/** One page of a search, under the key every list of pages shares. */
function pageQuery(asked: SearchPostingsQuery) {
    return {
        queryKey: keys.catalogue.page(asked),
        queryFn: () => searchPostings(asked),
        staleTime: SEARCH_FRESH_MS,
    };
}

/** Runs `run` once the browser has nothing else to do. Returns its cancel. */
export function whenIdle(run: () => void): () => void {
    if (typeof window.requestIdleCallback === "function") {
        const handle = window.requestIdleCallback(run, { timeout: 2_000 });
        return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(run, 200);
    return () => window.clearTimeout(handle);
}

/**
 * Fetches the page after this one while the browser is idle, so Next or
 * Show more has it already. Nothing when there is no next page.
 */
function usePrefetchNext(query: SearchPostingsQuery, cursor: string | null | undefined) {
    const client = useQueryClient();
    const asked = cursor ? JSON.stringify({ ...query, next_cursor: cursor }) : null;
    useEffect(() => {
        if (!asked) return;
        return whenIdle(() => void prefetchPage(client, JSON.parse(asked)));
    }, [client, asked]);
}

function prefetchPage(client: QueryClient, asked: SearchPostingsQuery) {
    return client.prefetchQuery(pageQuery(asked));
}

/** How the catalogue's reading stands. It changes all day, so it is looked at again each minute. */
export function useCatalogueStatus() {
    return useQuery({
        queryKey: keys.catalogue.status,
        queryFn: getCatalogueStatus,
        refetchInterval: 60_000,
    });
}

/**
 * A search, a page at a time, forward only: each page carries the cursor
 * for the next, and a cursor is good only with the same search. Each page
 * is read through the page cache, so the page fetched ahead is the one
 * shown. `ahead` fetches the next page while the browser is idle.
 */
export function useSearch(query: SearchPostingsQuery, enabled = true, ahead = true) {
    const client = useQueryClient();
    const search = useInfiniteQuery({
        queryKey: keys.catalogue.search(query),
        queryFn: ({ pageParam }) =>
            client.fetchQuery(pageQuery(pageParam ? { ...query, next_cursor: pageParam } : query)),
        initialPageParam: "",
        // A cursor already walked leads back over pages already shown: the end.
        getNextPageParam: (page, _pages, _last, walked) =>
            page.next_cursor && !walked.includes(page.next_cursor) ? page.next_cursor : undefined,
        enabled,
        staleTime: SEARCH_FRESH_MS,
    });
    const last = search.data?.pages.at(-1);
    usePrefetchNext(query, enabled && ahead ? last?.next_cursor : null);
    return search;
}

/** How many a search finds, and how they divide. Null asks nothing. */
export function useSearchCounts(query: SearchPostingsQuery | null) {
    return useQuery({
        queryKey: keys.catalogue.counts(query),
        queryFn: () => searchCounts(query ?? {}),
        enabled: query !== null,
        placeholderData: keepPreviousData,
        staleTime: SEARCH_FRESH_MS,
    });
}

/** Places to pick from, for the envoy's where. */
export function usePlaces(
    query: {
        level: PlaceLevel;
        country?: readonly string[];
        region?: readonly string[];
        q?: string;
        limit?: number;
    },
    enabled = true,
) {
    return useQuery({
        queryKey: keys.catalogue.places(query),
        queryFn: () => getPlaces(query),
        enabled,
        staleTime: 5 * 60_000,
        placeholderData: keepPreviousData,
    });
}

/** Where the visitor is, reused briefly on reload. A failure is no place, not an error to show. */
export function useHere(enabled = true) {
    return useQuery({
        queryKey: keys.catalogue.here,
        queryFn: getHere,
        enabled,
        staleTime: SEARCH_FRESH_MS,
        retry: false,
    });
}

/** Job titles to pick from, as a person types one. */
export function useTitles(q: string) {
    return useQuery({
        queryKey: keys.catalogue.titles(q),
        queryFn: () => getTitles(q || undefined, 8),
        staleTime: 5 * 60_000,
        placeholderData: keepPreviousData,
    });
}

/**
 * One page of a search, by the cursor the page before gave: what a
 * Previous and Next list walks. A cursor is good only with its search.
 */
export function useSearchPage(
    query: SearchPostingsQuery,
    cursor: string | undefined,
    enabled = true,
) {
    const asked = cursor ? { ...query, next_cursor: cursor } : query;
    const page = useQuery({ ...pageQuery(asked), enabled, placeholderData: keepPreviousData });
    // Not from a placeholder: its cursor belongs to the page before.
    usePrefetchNext(query, page.isPlaceholderData ? null : page.data?.next_cursor);
    return page;
}

/** One posting, open or closed, with its text. */
export function usePosting(postingId: string | null) {
    return useQuery({
        queryKey: keys.catalogue.posting(postingId ?? ""),
        queryFn: () => getPosting(postingId as string),
        enabled: Boolean(postingId),
    });
}

/** Each read as a posting, null for one that failed, and whether any is still read. */
function readPostings(results: readonly UseQueryResult<SearchPosting>[]): {
    reads: (SearchPosting | null | undefined)[];
    pending: boolean;
} {
    return {
        reads: results.map((read) => (read.isError ? null : read.data)),
        pending: results.some((read) => read.isPending),
    };
}

/**
 * Each of these postings, open or closed, under the key one posting is
 * read by: null for one the catalogue could not give, absent while one is
 * read, and whether any still is. The same reads give the same record, so
 * a memo over it holds.
 */
export function usePostings(postingIds: readonly string[]): {
    postings: Record<string, SearchPosting | null | undefined>;
    pending: boolean;
} {
    // Hoisted, so the query keeps the combined result while the reads stand.
    const { reads, pending } = useQueries({
        queries: postingIds.map((postingId) => ({
            queryKey: keys.catalogue.posting(postingId),
            queryFn: (): Promise<SearchPosting> => getPosting(postingId),
        })),
        combine: readPostings,
    });
    const postings = useMemo(
        () => Object.fromEntries(postingIds.map((postingId, at) => [postingId, reads[at]])),
        [postingIds, reads],
    );
    return { postings, pending };
}

/** The posting's record, in order. */
export function usePostingEvents(postingId: string | null) {
    return useQuery({
        queryKey: keys.catalogue.postingEvents(postingId ?? ""),
        queryFn: async () => (await getPostingEvents(postingId as string)).data,
        enabled: Boolean(postingId),
    });
}
